import React, { useCallback, useMemo } from 'react';
import { useButtonPress } from '../../hooks/useButtonPress';

/**
 * Reusable button primitive — pill-shaped, reference dashboard style.
 *
 * All app actions should use this component (or IconButton) for consistent
 * hover, active, loading, and disabled feedback.
 *
 * variant: 'primary' | 'secondary' | 'ghost' | 'danger'
 * size:    'sm' | 'md' | 'lg'
 */
export default function Button({
  children,
  variant = 'secondary',
  size = 'md',
  disabled = false,
  loading = false,
  loadingText,
  icon = null,
  iconRight = null,
  onClick,
  type = 'button',
  className = '',
  style = {},
  'aria-label': ariaLabel,
  title,
}) {
  const { pressed, pressHandlers } = useButtonPress({ disabled, loading });

  const sizeClass = {
    sm: 'app-btn-pill app-btn-pill--sm',
    md: '',
    lg: 'app-btn-pill--lg',
  }[size] || '';

  const variantClass = {
    primary: 'app-btn-pill--primary',
    secondary: 'app-btn-pill--secondary',
    ghost: 'app-btn-pill--ghost',
    danger: 'app-btn-pill--danger',
  }[variant] || 'app-btn-pill--secondary';

  const heightStyle =
    size === 'sm' ? { height: 30, fontSize: 12, padding: '0 12px' } :
    size === 'lg' ? { height: 40, fontSize: 14, padding: '0 18px' } :
                    {};

  const handleClick = useCallback(
    (event) => {
      if (disabled || loading) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    },
    [disabled, loading, onClick]
  );

  const iconSlot = useMemo(() => {
    if (loading && icon && React.isValidElement(icon)) {
      const mergedClass = [icon.props.className, 'app-btn-pill__icon--spin']
        .filter(Boolean)
        .join(' ');
      return (
        <span className="app-btn-pill__icon-slot shrink-0" aria-hidden>
          {React.cloneElement(icon, { className: mergedClass })}
        </span>
      );
    }
    if (loading && !icon) {
      return (
        <span className="app-btn-pill__icon-slot shrink-0" aria-hidden>
          <svg
            className="app-btn-pill__spinner"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            aria-hidden
          >
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </span>
      );
    }
    if (icon) {
      return <span className="app-btn-pill__icon-slot shrink-0">{icon}</span>;
    }
    return null;
  }, [icon, loading]);

  const label = loading && loadingText != null ? loadingText : children;
  const busyLabel = loading && loadingText != null ? loadingText : ariaLabel || (typeof children === 'string' ? children : undefined);

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-label={busyLabel}
      aria-busy={loading || undefined}
      title={title}
      data-pressed={pressed ? 'true' : undefined}
      data-loading={loading ? 'true' : undefined}
      className={[
        'app-btn-pill',
        variantClass,
        sizeClass,
        loading ? 'app-btn-pill--loading' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ ...heightStyle, ...style }}
      {...pressHandlers}
    >
      {iconSlot}
      <span className="app-btn-pill__label">{label}</span>
      {!loading && iconRight && <span className="app-btn-pill__icon-slot shrink-0">{iconRight}</span>}
    </button>
  );
}
